import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ladderAPI, challengeAPI, activityAPI } from '../lib/api';
import { Trophy, Lightning, Crown, Medal, ClockCounterClockwise, Pause, Warning, Hourglass, ArrowRight, CheckCircle, BellSimpleRinging, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import RankMovement from '../components/RankMovement';
import ConfirmDialog from '../components/ConfirmDialog';
import { LadderRowSkeleton, SkeletonList } from '../components/Skeleton';
import { targetInRangeFor } from '../lib/challengeEligibility';
import EventPhotoGallery from '../components/events/EventPhotoGallery';

export default function LadderPage() {
  const [players, setPlayers] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFeed, setShowFeed] = useState(false);
  const [pendingChallenge, setPendingChallenge] = useState(null); // {id, name}
  const [ladderMeta, setLadderMeta] = useState(null); // { id, slug, name, gender }
  const [viewerOnLadder, setViewerOnLadder] = useState(false);
  const { player: myPlayer, user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Phase 1 multi-ladder: `/ladder` loads the default singles-ladder for
  // back-compat; `/ladder/:slug` loads a specific ladder.
  const { slug: routeSlug } = useParams();
  const ladderSlug = routeSlug || 'singles-ladder';

  const fetchLadder = useCallback(async () => {
    setLoading(true);
    try {
      const [ladderRes, activityRes] = await Promise.all([
        ladderAPI.getAll(ladderSlug),
        activityAPI.getFeed().catch(() => ({ data: { events: [] } })),
      ]);
      setPlayers(ladderRes.data.players);
      setLadderMeta(ladderRes.data.ladder || null);
      setViewerOnLadder(!!ladderRes.data.viewer_on_ladder);
      setEvents(activityRes.data.events || []);
    } catch (err) {
      console.error('Failed to load ladder:', err);
      // 404 = unknown slug → bounce to default ladder.
      if (err.response?.status === 404) {
        toast.error('Ladder not found.');
        navigate('/ladder', { replace: true });
      }
    } finally {
      setLoading(false);
    }
  }, [ladderSlug, navigate]);

  // Always re-fetch when this route is navigated to (not just first mount).
  // location.key changes on every navigation, so the user returning from
  // /challenges after an approved match pulls a fresh ladder + fresh rank
  // without requiring a hard refresh.
  useEffect(() => {
    fetchLadder();
    // Sync the cached AuthContext player (rank/status) with the server so
    // downstream components (header, etc.) don't show stale rank either.
    refreshUser?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, ladderSlug]);

  // Viewer's own LADDER row is the source of truth for rank + status.
  // Multi-ladder: the AuthContext `myPlayer` may be from a different ladder
  // (defaults to singles-ladder); for the current ladder we look up by
  // profile_id matching the logged-in user.
  const myLadderRow = useMemo(() => {
    if (!user?.id) return null;
    return players.find(p => p.profile_id === user.id || p.player_id === myPlayer?.player_id) || null;
  }, [players, user?.id, myPlayer?.player_id]);
  const viewerRank = myLadderRow?.rank ?? null;
  const viewerStatus = myLadderRow?.status ?? null;
  const myOutgoing = myLadderRow?.outgoing_challenge || null;
  const myIncoming = myLadderRow?.incoming_challenge || null;

  // The viewer and one specific target share an active challenge if the viewer's
  // outgoing or incoming points at that target. Returns the challenge object when shared.
  const sharedChallenge = (target) => {
    if (!myPlayer) return null;
    if (myOutgoing && myOutgoing.opponent_id === target.player_id) return myOutgoing;
    if (myIncoming && myIncoming.opponent_id === target.player_id) return myIncoming;
    return null;
  };

  // Build a viewer-row shape the shared eligibility helper understands. Sourced
  // from the live ladder row (or the cached myPlayer) so we stay in sync.
  const viewerRow = myLadderRow || (myPlayer && {
    player_id: myPlayer.player_id,
    rank: myPlayer.rank,
    status: myPlayer.status,
    outgoing_challenge: null,
    incoming_challenge: null,
  });

  // True when target sits inside the viewer's challenge range by rank +
  // status. Pass full standings so the helper can honor the
  // nearest-4-active rule (skips paused players when counting slots).
  const inRange = (target) => targetInRangeFor(viewerRow, target, players);

  // Resolve the single action/status to show for this row. Order matters —
  // this is the canonical priority chain requested by the product spec:
  //   1. Viewer and target already share a challenge → View Match
  //      (evaluated BEFORE the range check — once a challenge exists, the
  //      opponent's row must always show View Match even when the opponent
  //      sits below the viewer in rank, e.g. a higher-ranked defender
  //      viewing the row of a lower-ranked challenger.)
  //   2. Viewer already has an outgoing challenge (to anyone) → Ineligible
  //      (viewer is the blocker; target may still be free)
  //   3. Target is tied up in their own challenge → vs {target opponent}
  //      (target is the blocker; label names the TARGET'S opponent)
  //   4. Otherwise → Challenge
  // Out-of-range rows (after shared-challenge precedence) return null.
  const rowAction = (target) => {
    // Priority 1 — always wins, regardless of range.
    const shared = sharedChallenge(target);
    if (shared) return { kind: 'view_match', challenge: shared };

    if (!inRange(target)) return null;

    // Priority 2 — viewer is the blocker (their one outgoing is taken).
    if (myOutgoing) return { kind: 'ineligible' };

    // Priority 3 — target is the blocker.
    const targetBlock = target.incoming_challenge || target.outgoing_challenge;
    if (targetBlock) {
      const name = targetBlock.opponent_name || '';
      const first = name.split(' ')[0] || 'opponent';
      return { kind: 'vs', label: `vs ${first}` };
    }

    // Priority 4 — default.
    return { kind: 'challenge' };
  };

  const requestChallenge = (target) => {
    // Opens the confirmation modal; actual POST happens in `confirmChallenge`
    setPendingChallenge({ id: target.player_id, name: target.name });
  };

  const confirmChallenge = async () => {
    if (!pendingChallenge) return;
    const { id } = pendingChallenge;
    setPendingChallenge(null);
    try {
      await challengeAPI.create(id);
      toast.success('Challenge sent!');
      fetchLadder();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send challenge');
    }
  };

  // Self-join handler for approved non-ladder users (Slice 1 endpoint).
  // Disabled-while-loading + duplicate-friendly. On success we refresh the
  // standings AND the auth context so downstream UI immediately treats the
  // viewer as a ladder member. We also surface a one-time dismissible
  // success card with a soft SMS-notifications nudge to Settings —
  // optional, never blocking, never auto-enabled.
  const [joining, setJoining] = useState(false);
  const [joinedRank, setJoinedRank] = useState(null);
  const handleJoinLadder = async () => {
    if (joining) return;
    setJoining(true);
    try {
      const res = await ladderAPI.join(ladderSlug);
      const rank = res.data?.rank ?? null;
      setJoinedRank(rank);
      await Promise.all([fetchLadder(), refreshUser?.()]);
    } catch (err) {
      if (err.response?.status === 409) {
        toast.info("You're already on this ladder. Refreshing…");
        await Promise.all([fetchLadder(), refreshUser?.()]);
      } else {
        const detail = err.response?.data?.detail;
        const msg = (detail && typeof detail === 'object' && detail.message) || detail || 'Failed to join the ladder.';
        toast.error(msg);
      }
    } finally {
      setJoining(false);
    }
  };

  const top3 = players.slice(0, 3);
  const rest = players.slice(3);
  const activeCount = players.filter(p => p.status === 'active').length;
  const pausedCount = players.filter(p => p.status === 'paused').length;

  if (loading) {
    return (
      <div className="space-y-6" data-testid="ladder-loading">
        <div className="px-1">
          <h1 className="font-serif text-4xl sm:text-5xl text-[#013523] tracking-tight">
            {ladderMeta?.name || 'The Ladder'}
          </h1>
        </div>
        <SkeletonList count={6} Component={LadderRowSkeleton} />
      </div>
    );
  }

  // Approved RTC member who is NOT on THIS ladder yet. We render the full
  // standings view plus a Join banner. Challenge UI stays hidden because
  // `myLadderRow` is null → row actions all return null.
  const showJoinBanner = !viewerOnLadder && !!user;

  return (
    <div className="space-y-6" data-testid="ladder-page">
      {/* Phase 1.5: Ladder lives at /ladder. This breadcrumb gives existing
          users a one-tap return to the new Home dashboard. */}
      <button
        type="button"
        onClick={() => navigate('/')}
        data-testid="ladder-back-to-home"
        className="flex items-center gap-1.5 text-sm text-[#6a766e] hover:text-[#013523] transition-colors -mb-2"
      >
        <span aria-hidden="true">←</span> Home
      </button>
      <div className="px-1">
        <h1
          data-testid="ladder-club-name"
          className="font-serif text-4xl sm:text-5xl text-[#013523] tracking-tight"
        >
          {ladderMeta?.name || 'The Ladder'}
        </h1>
        <p className="text-sm text-[#6a766e] mt-1">
          {activeCount} active{pausedCount > 0 && <span> · {pausedCount} paused</span>}
        </p>
      </div>

      {ladderMeta?.id && (
        <EventPhotoGallery flyerUrl={ladderMeta.flyer_url} eventName={ladderMeta.name} kind="ladder" eventId={ladderMeta.id} />
      )}

      {/* Slice 4 — approved RTC user who isn't on the ladder yet. They can
          still view standings (Slice 1 relaxed the endpoint); this card is
          an invitation to join, not a warning. */}
      {showJoinBanner && (
        <div
          className="bg-[#013523] text-white rounded-2xl p-5 sm:p-6 shadow-[0_10px_30px_rgba(1,53,35,0.18)] relative overflow-hidden"
          data-testid="ladder-join-banner"
        >
          <span aria-hidden="true" className="absolute left-0 top-0 bottom-0 w-1 bg-[#c2a251]" />
          <div className="flex items-start gap-4">
            <div className="shrink-0 w-11 h-11 rounded-xl bg-[#c2a251]/15 text-[#c2a251] flex items-center justify-center ring-1 ring-[#c2a251]/30">
              <Trophy size={20} weight="fill" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="font-serif text-xl sm:text-2xl text-white tracking-tight">
                Want to join the ladder?
              </h2>
              <p className="text-sm text-white/80 mt-1.5 leading-relaxed">
                You can view the standings now. Join the ladder when you&apos;re
                ready to start challenging other players.
              </p>
              <button
                type="button"
                data-testid="ladder-join-cta"
                disabled={joining}
                onClick={handleJoinLadder}
                className="mt-4 inline-flex items-center gap-1.5 bg-[#c2a251] hover:bg-[#b6923c] active:scale-[0.98] transition-all text-[#013523] text-sm font-semibold rounded-full px-4 py-2 shadow-[0_4px_14px_rgba(194,162,81,0.35)] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {joining ? 'Joining…' : 'Join Ladder'}
                {!joining && <ArrowRight size={14} weight="bold" />}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Post-join success card. Surfaced once after a successful self-join
          with a soft, optional SMS-notifications nudge. Dismissible. Never
          auto-enables SMS — that's a separate consent in Settings. */}
      {!showJoinBanner && joinedRank != null && (
        <div
          className="bg-white border border-[#cfd9d2] rounded-2xl p-5 sm:p-6 relative overflow-hidden"
          data-testid="ladder-join-success"
        >
          <span aria-hidden="true" className="absolute left-0 top-0 bottom-0 w-1 bg-[#015e3f]" />
          <button
            type="button"
            aria-label="Dismiss"
            data-testid="ladder-join-success-dismiss"
            onClick={() => setJoinedRank(null)}
            className="absolute top-3 right-3 text-[#6a766e] hover:text-[#013523] p-1 rounded-full"
          >
            <X size={14} weight="bold" />
          </button>
          <div className="flex items-start gap-4">
            <div className="shrink-0 w-11 h-11 rounded-xl bg-[#015e3f]/12 text-[#015e3f] flex items-center justify-center">
              <CheckCircle size={22} weight="fill" />
            </div>
            <div className="flex-1 min-w-0">
              <h2
                className="font-serif text-xl sm:text-2xl text-[#013523] tracking-tight"
                data-testid="ladder-join-success-title"
              >
                You&apos;re in. You were added at rank #{joinedRank}.
              </h2>
              <p className="text-sm text-[#3a4a40] mt-1.5 leading-relaxed">
                Welcome to the ladder. Browse the standings and challenge a
                player up to four spots above you whenever you&apos;re ready.
              </p>

              {/* SMS nudge — explicitly optional. We do not enable SMS here;
                  Settings owns the existing consent + phone-number flow. */}
              <div
                className="mt-4 bg-[#fbfaf8] border border-[#e3dcd1] rounded-xl px-4 py-3 flex items-start gap-3"
                data-testid="ladder-join-sms-nudge"
              >
                <div className="shrink-0 w-8 h-8 rounded-lg bg-[#c2a251]/15 text-[#7a6324] flex items-center justify-center">
                  <BellSimpleRinging size={16} weight="fill" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#013523] font-medium">
                    Want challenge alerts?
                  </div>
                  <p className="text-xs text-[#6a766e] mt-0.5 leading-relaxed">
                    Enable SMS notifications in Settings so you can be notified
                    when someone challenges you. Totally optional.
                  </p>
                  <button
                    type="button"
                    data-testid="ladder-join-sms-cta"
                    onClick={() => navigate('/settings')}
                    className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#015e3f] hover:underline"
                  >
                    Open Settings <ArrowRight size={12} weight="bold" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Active-challenge banner(s) — restored from earlier build.
          Row-level "View Pending Challenge" buttons are unchanged; this is an
          additive top-of-page status alert that links to /challenges. */}
      {(myOutgoing || myIncoming) && (
        <div className="space-y-2" data-testid="challenge-banner-group">
          {myOutgoing && (
            <ChallengeBanner
              direction="outgoing"
              challenge={myOutgoing}
              viewerId={myPlayer?.player_id}
              onView={() => navigate('/challenges')}
            />
          )}
          {myIncoming && (
            <ChallengeBanner
              direction="incoming"
              challenge={myIncoming}
              viewerId={myPlayer?.player_id}
              onView={() => navigate('/challenges')}
            />
          )}
        </div>
      )}

      {/* Top 3 Podium — desktop only. Mobile shows top-3 as compact rows below. */}
      {top3.length > 0 && (
        <div className="hidden sm:grid grid-cols-3 gap-3" data-testid="ladder-podium">
          <PodiumCard player={top3[1]} rank={2} myPlayer={myPlayer}
            action={top3[1] && rowAction(top3[1])}
            onChallenge={() => top3[1] && requestChallenge(top3[1])}
            onView={() => top3[1] && navigate(`/player/${top3[1].player_id}`)}
            onViewPending={() => navigate('/challenges')} />
          <PodiumCard player={top3[0]} rank={1} myPlayer={myPlayer}
            action={rowAction(top3[0])}
            onChallenge={() => requestChallenge(top3[0])}
            onView={() => navigate(`/player/${top3[0].player_id}`)}
            onViewPending={() => navigate('/challenges')} />
          <PodiumCard player={top3[2]} rank={3} myPlayer={myPlayer}
            action={top3[2] && rowAction(top3[2])}
            onChallenge={() => top3[2] && requestChallenge(top3[2])}
            onView={() => top3[2] && navigate(`/player/${top3[2].player_id}`)}
            onViewPending={() => navigate('/challenges')} />
        </div>
      )}

      {/* Mobile-only top 3 — same row layout as the rest, with podium icons. */}
      {top3.length > 0 && (
        <div className="sm:hidden space-y-2" data-testid="ladder-podium-mobile">
          {top3.map((p, i) => (
            <LadderRow
              key={p.player_id}
              player={p}
              isMe={myPlayer?.player_id === p.player_id}
              action={rowAction(p)}
              onChallenge={() => requestChallenge(p)}
              onClick={() => navigate(`/player/${p.player_id}`)}
              onViewPending={() => navigate('/challenges')}
              delay={i}
              podiumRank={p.rank}
            />
          ))}
        </div>
      )}

      {/* Rest of ladder */}
      <div className="space-y-2">
        {rest.map((p, i) => (
          <LadderRow
            key={p.player_id}
            player={p}
            isMe={myPlayer?.player_id === p.player_id}
            action={rowAction(p)}
            onChallenge={() => requestChallenge(p)}
            onClick={() => navigate(`/player/${p.player_id}`)}
            onViewPending={() => navigate('/challenges')}
            delay={i}
          />
        ))}
      </div>

      {players.length === 0 && (
        <div className="text-center py-20">
          <Trophy size={48} weight="duotone" className="text-[#e3dcd1] mx-auto mb-4" />
          <p className="text-[#6a766e] font-serif text-xl">No players yet</p>
          <p className="text-sm text-[#6a766e] mt-1">Invite players to start the competition</p>
        </div>
      )}

      {/* Activity feed toggle */}
      {events.length > 0 && (
        <div>
          <button
            data-testid="toggle-activity-feed"
            onClick={() => setShowFeed(!showFeed)}
            className="flex items-center gap-2 text-sm text-[#6a766e] hover:text-[#015e3f] transition-colors"
          >
            <ClockCounterClockwise size={18} weight="duotone" />
            <span>{showFeed ? 'Hide' : 'Show'} Recent Activity ({events.length})</span>
          </button>
          {showFeed && (
            <div className="mt-3 space-y-1" data-testid="activity-feed">
              {events.slice(0, 15).map(evt => (
                <div key={evt.event_id} className="flex gap-3 py-2 border-l-2 border-[#e3dcd1] pl-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[#2a332c] leading-snug">{evt.description}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <ConfirmDialog
        open={!!pendingChallenge}
        title="Send challenge?"
        message={pendingChallenge ? `Are you sure you want to challenge ${pendingChallenge.name}?` : ''}
        confirmLabel="Challenge"
        onConfirm={confirmChallenge}
        onCancel={() => setPendingChallenge(null)}
        testId="confirm-challenge-dialog"
      />

    </div>
  );
}

function PodiumCard({ player, rank, myPlayer, action, onChallenge, onView, onViewPending }) {
  if (!player) return <div />;
  const isPaused = player.status === 'paused';
  const colors = {
    1: { bg: 'bg-[#c2a251]/10', border: 'border-[#c2a251]', text: 'text-[#c2a251]' },
    2: { bg: 'bg-[#8e8e8e]/10', border: 'border-[#8e8e8e]', text: 'text-[#8e8e8e]' },
    3: { bg: 'bg-[#b87333]/10', border: 'border-[#b87333]', text: 'text-[#b87333]' },
  };
  const c = colors[rank];
  const isMe = myPlayer?.player_id === player.player_id;
  const Icon = rank === 1 ? Crown : Medal;

  return (
    <div
      data-testid={`podium-rank-${rank}`}
      onClick={onView}
      className={`cursor-pointer ${isPaused ? 'bg-[#f0ece3]/60 border-[#e3dcd1] opacity-60' : `${c.bg} ${c.border}`} rounded-2xl border p-3 text-center transition-all hover:-translate-y-1 hover:shadow-md ${rank === 1 ? 'self-start -mt-2' : 'self-end mt-2'}`}
    >
      <Icon size={rank === 1 ? 28 : 22} weight="duotone" className={`${isPaused ? 'text-[#b0a99a]' : c.text} mx-auto mb-1`} />
      <div className={`font-serif text-2xl font-semibold ${isPaused ? 'text-[#b0a99a]' : c.text}`}>#{rank}</div>
      <div className={`text-sm font-medium truncate mt-2 ${isPaused ? 'text-[#9a958c]' : isMe ? 'text-[#015e3f]' : 'text-[#2a332c]'}`}>
        {isMe ? 'You' : player.name}
      </div>
      <div className="text-[10px] text-[#6a766e] mt-0.5 flex items-center justify-center gap-1.5">
        {isPaused ? (
          <span className="inline-flex items-center gap-0.5 text-[#b0a99a] font-medium"><Pause size={10} weight="fill" /> Paused</span>
        ) : (
          <>
            <span>{player.wins}W - {player.losses}L</span>
            <RankMovement movement={player.movement} testId={`podium-movement-${rank}`} />
          </>
        )}
      </div>
      {action?.kind === 'view_match' ? (
        <button
          data-testid={`view-pending-podium-${rank}`}
          onClick={(e) => { e.stopPropagation(); onViewPending(); }}
          className="mt-2 text-[10px] bg-[#fbfaf8] border border-[#015e3f] text-[#015e3f] rounded-full px-3 py-1 font-medium transition-transform active:scale-95"
        >
          View Match
        </button>
      ) : action?.kind === 'ineligible' ? (
        <span
          data-testid={`ineligible-pill-podium-${rank}`}
          title="You already have an active outgoing challenge"
          className="mt-2 inline-flex items-center rounded-full bg-[#6a766e]/15 text-[#6a766e] px-2.5 py-1 text-[10px] font-medium whitespace-nowrap"
        >
          Ineligible
        </span>
      ) : action?.kind === 'vs' ? (
        <button
          type="button"
          data-testid={`locked-pill-podium-${rank}`}
          onClick={(e) => { e.stopPropagation(); onViewPending(); }}
          title={`${player.name} is currently in another match`}
          className="mt-2 inline-flex items-center gap-1 rounded-full border border-dashed border-[#c2a251]/60 bg-[#c2a251]/10 text-[#7a6324] px-2.5 py-1 text-[10px] font-medium whitespace-nowrap transition-colors hover:bg-[#c2a251]/20"
        >
          {action.label}
        </button>
      ) : action?.kind === 'challenge' ? (
        <button
          data-testid={`challenge-podium-${rank}`}
          onClick={(e) => { e.stopPropagation(); onChallenge(); }}
          className="mt-2 text-[10px] bg-[#015e3f] text-white rounded-full px-3 py-1 font-medium transition-transform active:scale-95"
        >
          Challenge
        </button>
      ) : null}
    </div>
  );
}

function LadderRow({ player, isMe, action, onChallenge, onClick, onViewPending, delay, podiumRank }) {
  const isPaused = player.status === 'paused';
  const stopAndGo = (e, fn) => { e.stopPropagation(); fn(); };

  // Podium decoration for ranks 1-3 when this row is used on mobile
  // in place of the podium cards. Icon appears inside the rank badge.
  const podiumTint = {
    1: { bg: 'bg-[#c2a251]/15', text: 'text-[#c2a251]', Icon: Crown },
    2: { bg: 'bg-[#8e8e8e]/15', text: 'text-[#5a5a5a]', Icon: Medal },
    3: { bg: 'bg-[#b87333]/15', text: 'text-[#b87333]', Icon: Medal },
  };
  const pod = podiumRank ? podiumTint[podiumRank] : null;

  return (
    <div
      data-testid={`ladder-row-${player.rank}`}
      className={`flex items-center gap-3 rounded-2xl border px-4 py-3 transition-all animate-fade-in-up ${
        isPaused
          ? 'bg-[#f0ece3]/50 border-[#e3dcd1] opacity-60'
          : `bg-[#fbfaf8] border-[#e3dcd1] hover:-translate-y-0.5 hover:shadow-md hover:border-[#015e3f]/30 ${isMe ? 'ring-2 ring-[#015e3f]/20 border-[#015e3f]/40' : ''}`
      }`}
      style={{ animationDelay: `${Math.min(delay, 5) * 0.05}s`, opacity: 0 }}
    >
      {/* Rank cluster.
          Desktop (sm+): fixed-width cluster with the movement chip to the
          right of the rank, so name columns align across ALL rows.
          Mobile: only the rank badge — the movement chip is moved to the
          secondary metadata line (under the player name) so the main row
          can give full width to the player name. */}
      <div className="shrink-0 flex items-center gap-1.5">
        {pod ? (
          <div
            className={`h-10 w-14 rounded-full flex items-center justify-center gap-1 font-serif text-lg font-semibold shrink-0 ${
              isPaused ? 'bg-[#e3dcd1] text-[#9a958c]' : `${pod.bg} ${pod.text}`
            }`}
            data-testid={`podium-badge-${podiumRank}`}
          >
            <pod.Icon size={14} weight="fill" />
            <span>{player.rank}</span>
          </div>
        ) : (
          <div className={`w-10 h-10 rounded-full flex items-center justify-center font-serif text-lg font-semibold shrink-0 ${
            isPaused ? 'bg-[#e3dcd1] text-[#9a958c]' : 'bg-[#f0ece3] text-[#013523]'
          }`}>
            {player.rank}
          </div>
        )}
      </div>

      {/* Name + status (clickable) */}
      <button
        type="button"
        onClick={onClick}
        className="flex-1 min-w-0 text-left cursor-pointer"
        data-testid={`name-btn-${player.player_id}`}
      >
        <div className={`font-medium text-base truncate ${isPaused ? 'text-[#9a958c]' : isMe ? 'text-[#015e3f]' : 'text-[#2a332c]'} hover:underline`}>
          {player.name}
          {isMe && <span className="text-[10px] text-[#c2a251] font-sans ml-1">(You)</span>}
        </div>
        <div className="flex items-center gap-2 flex-wrap mt-0.5">
          <span className={`text-xs ${isPaused ? 'text-[#b0a99a]' : 'text-[#6a766e]'}`}>{player.wins}W - {player.losses}L</span>
          {/* Movement pill — rendered inline with the W-L record on every
              viewport so the rank badge + player name above it always align
              identically across rows, regardless of whether a movement
              indicator is present. */}
          <span className="inline-flex">
            <RankMovement movement={player.movement} testId={`movement-${player.player_id}`} />
          </span>
          {isPaused && (
            <span className="inline-flex items-center gap-0.5 text-[10px] bg-[#e3dcd1] text-[#8a857c] rounded-full px-1.5 py-0.5" data-testid={`paused-badge-${player.player_id}`}>
              <Pause size={8} weight="fill" /> Paused
            </span>
          )}
        </div>
      </button>

      {/* Action: priority chain — View Match > Ineligible > vs X > Challenge > nothing */}
      {action?.kind === 'view_match' ? (
        <button
          data-testid={`view-pending-btn-${player.player_id}`}
          onClick={(e) => stopAndGo(e, onViewPending)}
          className="shrink-0 flex items-center gap-1.5 bg-[#fbfaf8] border border-[#015e3f] text-[#015e3f] rounded-full px-3 py-1.5 text-xs font-medium transition-transform active:scale-95 hover:bg-[#015e3f]/5"
        >
          View Match
        </button>
      ) : action?.kind === 'ineligible' ? (
        <span
          data-testid={`ineligible-pill-${player.player_id}`}
          title="You already have an active outgoing challenge"
          className="shrink-0 inline-flex items-center rounded-full bg-[#6a766e]/15 text-[#6a766e] px-2.5 py-1 text-[11px] font-medium whitespace-nowrap"
        >
          Ineligible
        </span>
      ) : action?.kind === 'vs' ? (
        <button
          type="button"
          data-testid={`locked-pill-${player.player_id}`}
          onClick={(e) => stopAndGo(e, onViewPending)}
          title={`${player.name} is currently in another match`}
          className="shrink-0 inline-flex items-center gap-1 rounded-full border border-dashed border-[#c2a251]/60 bg-[#c2a251]/10 text-[#7a6324] px-2.5 py-1 text-[11px] font-medium whitespace-nowrap transition-colors hover:bg-[#c2a251]/20"
        >
          {action.label}
        </button>
      ) : action?.kind === 'challenge' ? (
        <button
          data-testid={`challenge-btn-${player.player_id}`}
          onClick={(e) => stopAndGo(e, onChallenge)}
          className="shrink-0 flex items-center gap-1.5 bg-[#015e3f] text-white rounded-full px-3 py-1.5 text-xs font-medium transition-transform active:scale-95 hover:bg-[#014d34]"
        >
          <Lightning size={14} weight="fill" />
          Challenge
        </button>
      ) : null}
    </div>
  );
}


// ------------------------------------------------------------------
// ChallengeBanner — top-of-ladder status alert for the viewer's active
// challenges. One banner per direction (outgoing / incoming). Row-level
// "View Pending Challenge" buttons on each ladder row are unchanged; this
// banner is additive.
// ------------------------------------------------------------------
function ChallengeBanner({ direction, challenge, viewerId, onView }) {
  const opponent = challenge.opponent_name || 'your opponent';
  const status = challenge.status; // 'pending' | 'accepted' | 'awaiting_approval'

  // For awaiting_approval, the submitter is whoever set matches.reported_by;
  // the reviewer is the OTHER player. `direction` tells us who challenged
  // whom, not who submitted the score — those are independent. Use the
  // match's reported_by to pick the correct perspective.
  const viewerIsSubmitter =
    status === 'awaiting_approval' &&
    viewerId != null &&
    challenge.reported_by != null &&
    challenge.reported_by === viewerId;

  // Tone → emerald for accepted (playtime), amber for pending (waiting),
  // gold for awaiting_approval (needs action by reviewer) / soft for submitter.
  const toneMap = {
    pending: {
      wrap: 'bg-[#c2a251]/10 border-[#c2a251]/40 text-[#7a6324]',
      badge: 'bg-[#c2a251]/20 text-[#7a6324]',
      icon: Hourglass,
    },
    accepted: {
      wrap: 'bg-[#015e3f]/10 border-[#015e3f]/30 text-[#013523]',
      badge: 'bg-[#015e3f]/15 text-[#015e3f]',
      icon: Lightning,
    },
    awaiting_approval: {
      wrap: 'bg-[#c2a251]/15 border-[#c2a251]/50 text-[#7a6324]',
      badge: 'bg-[#c2a251]/25 text-[#7a6324]',
      icon: Warning,
    },
    // Softer tone when the viewer is the one waiting on the opponent to review.
    awaiting_approval_submitter: {
      wrap: 'bg-[#c2a251]/10 border-[#c2a251]/40 text-[#7a6324]',
      badge: 'bg-[#c2a251]/20 text-[#7a6324]',
      icon: Hourglass,
    },
  };
  const toneKey = viewerIsSubmitter ? 'awaiting_approval_submitter' : status;
  const tone = toneMap[toneKey] || toneMap.pending;
  const Icon = tone.icon;

  // Compose message. awaiting_approval is perspective-driven (submitter vs
  // reviewer); everything else is direction-driven (outgoing vs incoming).
  let label = '';
  let message = '';
  if (status === 'awaiting_approval') {
    if (viewerIsSubmitter) {
      label = 'Score submitted';
      message = `Waiting for ${opponent} to review.`;
    } else {
      label = 'Score needs approval';
      message = `${opponent} submitted a score. Review it now.`;
    }
  } else if (direction === 'outgoing') {
    if (status === 'pending') {
      label = 'Challenge sent';
      message = `Waiting for ${opponent} to respond. They have 72 hours to accept.`;
    } else if (status === 'accepted') {
      label = 'Challenge accepted';
      message = `${opponent} accepted. Get your match scheduled.`;
    }
  } else {
    if (status === 'pending') {
      label = 'Incoming challenge';
      message = `${opponent} challenged you. Accept or decline within 72 hours.`;
    } else if (status === 'accepted') {
      label = 'Match on';
      message = `You accepted ${opponent}'s challenge. Get your match scheduled.`;
    }
  }

  // "Action" badge appears when the VIEWER has something to do:
  //  - an incoming pending challenge they need to accept/decline
  //  - an awaiting_approval match where THEY are the reviewer (not submitter)
  const needsAction =
    (direction === 'incoming' && status === 'pending') ||
    (status === 'awaiting_approval' && !viewerIsSubmitter);

  return (
    <div
      className={`flex items-center gap-3 rounded-2xl border px-4 py-3 ${tone.wrap}`}
      data-testid={`challenge-banner-${direction}-${status}`}
      role="status"
    >
      <div className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${tone.badge}`}>
        <Icon size={18} weight="duotone" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold leading-tight flex items-center gap-2">
          {label}
          {needsAction && (
            <span className="text-[10px] tracking-[0.1em] uppercase bg-[#c2a251] text-white rounded-full px-1.5 py-0.5">
              Action
            </span>
          )}
        </div>
        <div className="text-xs leading-snug mt-0.5 opacity-90">{message}</div>
      </div>
      <button
        type="button"
        data-testid={`challenge-banner-cta-${direction}`}
        onClick={onView}
        className="shrink-0 flex items-center gap-1 bg-[#013523] text-white rounded-full px-3 py-1.5 text-xs font-medium active:scale-95 hover:bg-[#014d34] transition-transform"
      >
        View
        <ArrowRight size={12} weight="bold" />
      </button>
    </div>
  );
}
